import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TranslationProvider } from './context/TranslationContext';
import { PromoPopupProvider, usePromoPopupContext } from './context/PromoPopupContext';
import { FontLoader } from './components/FontLoader';
import ScrollToTop from './components/ScrollToTop';
import PromoPopup from './components/PromoPopup';
import { injectAnalytics, trackPageView } from './utils/analytics';
import { getApiBaseUrl } from './lib/apiBase';

const ANALYTICS_API_URL = getApiBaseUrl();

// Fetches analytics_config once and injects GA4/GTM/Meta Pixel/custom scripts
// on user-facing pages only — admins would skew analytics, so /admin/* is excluded.
const AnalyticsInjector = () => {
  const location = useLocation();
  const injectedRef = useRef(false);

  useEffect(() => {
    if (injectedRef.current) return;
    if (window.location.pathname.startsWith('/admin')) return;
    injectedRef.current = true;
    axios.get(`${ANALYTICS_API_URL}/settings`)
      .then((res) => {
        const config = res.data?.analytics_config;
        if (config) {
          window.__ga4Id = config.ga4_measurement_id;
          injectAnalytics(config);
        }
      })
      .catch(() => {}); // never crash the app over analytics
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
};

// Pages
import LandingPage from './pages/LandingPage';
import { LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';
import CourseCatalog from './pages/CourseCatalog';
import CourseDetail from './pages/CourseDetail';
import LessonPlayer from './pages/LessonPlayer';
import QuizPage from './pages/QuizPage';
import UserDashboard from './pages/UserDashboard';
import ProfilePage from './pages/ProfilePage';
import PricingPage from './pages/PricingPage';
import { PaymentCancel } from './pages/PaymentPages';
import ThankYou from './pages/ThankYou';
import LeaderboardPage from './pages/LeaderboardPage';
import StudentDirectoryPage from './pages/StudentDirectoryPage';
import { ProjectsPage, AffiliatePage } from './pages/CommunityPages';
import { 
  AdminLayout, 
  AdminDashboard, 
  AdminUsers, 
  AdminCourses,
  AdminSettings 
} from './pages/AdminPages';
import {
  AdminQuizzes,
  AdminVideoAnalytics,
  AdminProjects,
  AdminAffiliates,
  AdminRevenueAnalytics,
  AdminCertificateDesigner,
  AdminCategories,
  AdminShareRewards
} from './pages/AdminExtendedPages';
import { AdminLessons } from './pages/admin/AdminLessons';
import { AdminIntegrations } from './pages/admin/AdminIntegrations';
import { AdminTeamRoles } from './pages/admin/AdminTeamRoles';
import { AdminBackup } from './pages/admin/AdminBackup';
import { ServerMonitor } from './pages/admin/ServerMonitor';
import { AdminGSTSettings } from './pages/admin/AdminGSTSettings';
import { AdminCountryPricing } from './pages/admin/AdminCountryPricing';
import { AdminPaymentGateways } from './pages/admin/AdminPaymentGateways';
import { AdminBillingTax } from './pages/admin/AdminBillingTax';
import { AdminInvoices } from './pages/admin/AdminInvoices';
import { AdminLegalPages } from './pages/admin/AdminLegalPages';
import LegalPage from './pages/LegalPage';
import { AdminAnalyticsSettings } from './pages/admin/AdminAnalyticsSettings';
import { AdminCoupons } from './pages/admin/AdminCoupons';
import { AdminCRM } from './pages/admin/AdminCRM';
import { NoAccess } from './pages/admin/NoAccess';
import {
  AdminLiveClasses,
  AdminTranslations,
  AdminEmailNotifications
} from './pages/AdminAdvancedPages';
import LiveClassesPage from './pages/LiveClassesPage';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Preserve the page the user was trying to reach (e.g. a free lesson
    // clicked while logged out) so LoginPage's `location.state.from` sends
    // them back there instead of always landing on /dashboard.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Old /payment/success links (bookmarks, already-sent emails) now land on
// /thank-you — forward the query string (session_id/gateway/token) so the
// verification logic there still has what it needs.
const RedirectToThankYou = () => {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`/thank-you${qs ? `?${qs}` : ''}`} replace />;
};

// Gate a single admin sub-route on a permission (or super_admin)
const AdminRoute = ({ children, permission, superAdminOnly = false }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  if (superAdminOnly && !isSuperAdmin) return <NoAccess />;
  if (permission && !hasPermission(permission)) return <NoAccess />;
  return children;
};

// Renders the promo popup itself — kept out of admin routes and premium users.
const PromoPopupRenderer = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { showPopup, popupConfig, admissionCount, handleClose } = usePromoPopupContext();

  if (location.pathname.startsWith('/admin')) return null;
  // Share & Earn now lives as a permanent section on /pricing itself, so the
  // popup (which only offers the Upgrade CTA now) would be redundant there.
  if (location.pathname === '/pricing') return null;
  if (!user || user.is_premium) return null;
  if (!showPopup) return null;

  return (
    <PromoPopup
      onClose={handleClose}
      popupConfig={popupConfig}
      admissionCount={admissionCount}
    />
  );
};

function AppContent() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <FontLoader />
      <AnalyticsInjector />
      <PromoPopupProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/courses" element={<CourseCatalog />} />
        <Route path="/courses/:courseId" element={<CourseDetail />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/terms" element={<LegalPage slug="terms" />} />
        <Route path="/refund-policy" element={<LegalPage slug="refund-policy" />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/students" element={<StudentDirectoryPage />} />
        <Route path="/live-classes" element={<LiveClassesPage />} />
        
        {/* Protected Routes */}
        <Route path="/lessons/:lessonId" element={
          <ProtectedRoute><LessonPlayer /></ProtectedRoute>
        } />
        <Route path="/quiz/:lessonId" element={
          <ProtectedRoute><QuizPage /></ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute><UserDashboard /></ProtectedRoute>
        } />
        <Route path="/bookmarks" element={
          <ProtectedRoute><UserDashboard /></ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        } />
        <Route path="/affiliate" element={
          <ProtectedRoute><AffiliatePage /></ProtectedRoute>
        } />
        
        {/* Payment Routes */}
        <Route path="/thank-you" element={
          <ProtectedRoute><ThankYou /></ProtectedRoute>
        } />
        <Route path="/payment/success" element={<RedirectToThankYou />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        
        {/* Referral redirect */}
        <Route path="/ref/:code" element={<Navigate to="/signup" replace />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminRoute permission="manage_users"><AdminUsers /></AdminRoute>} />
          <Route path="crm" element={<AdminRoute permission="manage_crm"><AdminCRM /></AdminRoute>} />
          <Route path="courses" element={<AdminRoute permission="manage_courses"><AdminCourses /></AdminRoute>} />
          <Route path="categories" element={<AdminRoute permission="manage_courses"><AdminCategories /></AdminRoute>} />
          <Route path="lessons" element={<AdminRoute permission="manage_lessons"><AdminLessons /></AdminRoute>} />
          <Route path="quizzes" element={<AdminRoute permission="manage_quizzes"><AdminQuizzes /></AdminRoute>} />
          <Route path="certificates" element={<AdminRoute permission="manage_certificates"><AdminCertificateDesigner /></AdminRoute>} />
          <Route path="coupons" element={<AdminRoute permission="manage_coupons"><AdminCoupons /></AdminRoute>} />
          <Route path="revenue" element={<AdminRoute permission="view_revenue"><AdminRevenueAnalytics /></AdminRoute>} />
          <Route path="analytics" element={<AdminRoute permission="view_analytics"><AdminVideoAnalytics /></AdminRoute>} />
          <Route path="projects" element={<AdminRoute permission="manage_projects"><AdminProjects /></AdminRoute>} />
          <Route path="affiliates" element={<AdminRoute permission="manage_affiliates"><AdminAffiliates /></AdminRoute>} />
          <Route path="share-rewards" element={<AdminRoute permission="manage_marketing"><AdminShareRewards /></AdminRoute>} />
          <Route path="live-classes" element={<AdminRoute permission="manage_live_classes"><AdminLiveClasses /></AdminRoute>} />
          <Route path="translations" element={<AdminRoute permission="manage_settings"><AdminTranslations /></AdminRoute>} />
          <Route path="email" element={<AdminRoute permission="send_notifications"><AdminEmailNotifications /></AdminRoute>} />
          <Route path="integrations" element={<AdminRoute permission="manage_integrations"><AdminIntegrations /></AdminRoute>} />
          <Route path="settings" element={<AdminRoute permission="manage_settings"><AdminSettings /></AdminRoute>} />
          <Route path="gst-settings" element={<AdminRoute permission="manage_settings"><AdminGSTSettings /></AdminRoute>} />
          <Route path="country-pricing" element={<AdminRoute permission="manage_settings"><AdminCountryPricing /></AdminRoute>} />
          <Route path="payment-gateways" element={<AdminRoute permission="manage_integrations"><AdminPaymentGateways /></AdminRoute>} />
          <Route path="billing" element={<AdminRoute permission="manage_settings"><AdminBillingTax /></AdminRoute>} />
          <Route path="analytics-settings" element={<AdminRoute permission="manage_settings"><AdminAnalyticsSettings /></AdminRoute>} />
          <Route path="orders" element={<AdminRoute permission="view_revenue"><AdminInvoices /></AdminRoute>} />
          <Route path="legal" element={<AdminRoute permission="manage_settings"><AdminLegalPages /></AdminRoute>} />
          <Route path="team" element={<AdminRoute superAdminOnly><AdminTeamRoles /></AdminRoute>} />
          <Route path="backup" element={<AdminRoute superAdminOnly><AdminBackup /></AdminRoute>} />
          <Route path="server" element={<AdminRoute permission="manage_settings"><ServerMonitor /></AdminRoute>} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <PromoPopupRenderer />
      </PromoPopupProvider>
      <Toaster position="top-center" />
    </BrowserRouter>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <TranslationProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </TranslationProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
